import React from "react";
import PropTypes from "prop-types";
import { Oval } from "react-loader-spinner";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import Input from "components/input";
import { useTranslation } from "react-i18next";
import Select from "components/select";
import useCompanyDetails from "./useCompanyDetails";

function EditCompanyDetails({
  onDiscard = () => {},
  onSave = () => {},
  isLoading = false,
}) {
  const [t] = useTranslation();
  const userDetails = useSelector((state) => state.userDetails || {});
  const {
    companyName,
    representativeName,
    phone,
    country,
    city,
    companyWebsite,
    email,
  } = userDetails;

  const defaultsValues = {
    companyName: companyName || "",
    representativeName: representativeName || "",
    phoneNumber: phone || "",
    country: country?.id || "",
    city: city?.id || "",
    companyWebsite: companyWebsite || "",
    email: email || "",
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: defaultsValues,
  });

  const countryId = watch("country");

  const { countriesOptions, cities, citiesOptions } = useCompanyDetails({
    countryId,
  });

  return (
    <form
      className="flex flex-col self-stretch rounded-[16px] border-[1px] border-solid border-primary"
      onSubmit={handleSubmit(onSave)}
    >
      <div className="flex p-[24px] justify-between align-center self-stretch border-b border-solid border-[#E4E4E7]">
        <div className="text-[#1D1B22] font-semibold text-[24px]">
          {t("companyInformation")}
        </div>

        <div className="flex items-center gap-[12px]">
          <button
            type="button"
            onClick={onDiscard}
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-default-color bg-white h-[46px]"
          >
            <div className="text-base-black font-bold text-[16px] not-italic">
              {t("Cancel")}
            </div>
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-primary bg-primary h-[46px]"
          >
            <Oval
              height={30}
              width={30}
              color="#FFF"
              visible={isLoading}
              ariaLabel="oval-loading"
              secondaryColor="#FFF"
              strokeWidth={5}
              strokeWidthSecondary={3}
            />
            <div className="text-white font-bold text-[16px] not-italic">
              {t("saveChangesCta")}
            </div>
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-[20px] p-[24px] self-stretch">
        {/* ************** COMPANY NAME ***************/}
        <Input
          label={t("companyName")}
          isRequired={true}
          placeholder={t("placeHolderType", {
            title: t("companyName").toLowerCase(),
          })}
          name="companyName"
          register={register}
          registerArgs={{
            required: t("errorField", { field: t("companyName") }),
          }}
          errorMessage={errors.companyName ? errors.companyName.message : ""}
          labelClassName="text-primary font-semibold text-[16px]  "
          inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
            errors.companyName ? "border-red" : "border-default-color"
          } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
        />

        {/* ************** COUNTRY ***************/}
        <Select
          label={t("country")}
          placeholder={t("placeholderSelect", {
            field: t("country").toLowerCase(),
          })}
          name="country"
          containerClassName="flex flex-col gap-[10px] min-w-[250px] sm-max:w-full"
          value={
            watch("country")
              ? countriesOptions.find((elem) => elem.id === watch("country"))
              : null
          }
          onChange={(e) => {
            setValue("country", e.value);
            setValue("city", ""); // Reset city when country changes
          }}
          register={register}
          options={countriesOptions}
        />

        {/* ************** CITY ***************/}
        <Select
          label={t("city")}
          placeholder={t("placeholderSelect", {
            field: t("city").toLowerCase(),
          })}
          name="city"
          containerClassName="flex flex-col gap-[10px] min-w-[250px] sm-max:w-full"
          isDisabled={cities.length <= 0}
          value={
            watch("city")
              ? citiesOptions.find((elem) => elem.id === watch("city"))
              : null
          }
          onChange={(e) => {
            setValue("city", e.value);
          }}
          register={register}
          options={citiesOptions}
        />

        {/* ************** COMPANY WEBSITE ***************/}
        <Input
          label={t("companyWebsite")}
          placeholder={t("placeHolderType", {
            title: t("companyWebsite").toLowerCase(),
          })}
          type="url"
          name="companyWebsite"
          register={register}
          labelClassName="text-primary font-semibold text-[16px]"
          inputClassName="flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid border-default-color bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]"
        />

        <Input
          label={t("companyEmail")}
          placeholder={t("placeHolderType", {
            title: t("email").toLowerCase(),
          })}
          type="email"
          name="email"
          register={register}
          readOnly
          labelClassName="text-primary font-semibold text-[16px]"
          inputClassName="flex  h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border border-[#E4E4E7] bg-[#F4F4F5] outline-none text-dark-gray font-regular text-[16px] not-italic placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]"
        />

        {/* ************** PHONE NUMBER ***************/}
        <Input
          label={t("phoneNumber")}
          placeholder={t("placeholder", {
            field: t("phoneNumber").toLowerCase(),
          })}
          type="text"
          name="phoneNumber"
          register={register}
          labelClassName="text-primary font-semibold text-[16px]"
          inputClassName="flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid border-default-color bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px] no-arrows-on-input"
        />

        {/* ************** REPRESENTATIVE NAME ***************/}
        <Input
          label={t("representativeName")}
          isRequired={true}
          placeholder={t("placeHolderType", {
            title: t("representativeName").toLowerCase(),
          })}
          name="representativeName"
          register={register}
          registerArgs={{
            required: t("errorField", { field: t("representativeName") }),
          }}
          errorMessage={
            errors.representativeName ? errors.representativeName.message : ""
          }
          labelClassName="text-primary font-semibold text-[16px]"
          inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
            errors.representativeName ? "border-red" : "border-default-color"
          } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
        />
      </div>
    </form>
  );
}

EditCompanyDetails.propTypes = {
  onDiscard: PropTypes.func,
  onSave: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default EditCompanyDetails;
